import { animated, useSpring } from '@react-spring/web';
import clsx from 'clsx';
import { useEffect, useState } from 'react';
import styles from './Overlay.module.css';

type OverlayProps = {
  children: React.ReactNode;
  ariaLabel?: string;
  dataTestId?: string;
  onClose?: () => void;
};

export const Overlay = ({ children, ariaLabel, dataTestId, onClose }: OverlayProps) => {
  const animationProps = useSpring({
    from: { opacity: 0 },
    to: { opacity: 1 },
    delay: 150,
    config: { tension: 100, friction: 20 },
  });

  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    const timeout = setTimeout(() => setMounted(true), 50);
    return () => clearTimeout(timeout);
  }, []);

  useEffect(() => {
    const handleKeyDown = ({ key }: KeyboardEvent) => key === 'Escape' && onClose?.();

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [onClose]);

  return (
    <div
      className={styles.overlay}
      role="dialog"
      aria-modal="true"
      aria-label={ariaLabel}
      data-testid={dataTestId}
    >
      <div className={clsx(styles.backdrop, { [styles.visible]: mounted })} />
      <animated.div style={animationProps} className={styles.content}>
        {children}
      </animated.div>
    </div>
  );
};
